<script setup lang="ts">
import { computed, reactive, ref } from "vue";

import {
  draftBriefing,
  updateBriefingContent,
  type ProjectHazard,
  type WorkPackTaskBrief,
} from "@/api/client";
import SaveStatus from "@/components/SaveStatus.vue";
import SiteHazardsNotice from "@/components/SiteHazardsNotice.vue";

const props = defineProps<{
  briefingId: string;
  content: WorkPackTaskBrief | null;
  hazards?: ProjectHazard[];
}>();
const emit = defineEmits<{ saved: [] }>();

// The standard rail PPE set offered as toggles; supervisors add anything else.
const STANDARD_PPE = [
  "Hard hat",
  "Hi-vis",
  "Safety boots",
  "Gloves",
  "Eye protection",
  "Hearing protection",
  "Respirator (RPE)",
  "Harness / lanyard",
];

const present = computed(() => {
  const c = props.content;
  if (!c) return false;
  return Boolean(
    c.description ||
      c.location ||
      c.supervisor ||
      (c.hazards ?? []).length ||
      (c.ppe ?? []).length ||
      (c.plant ?? []).length ||
      (c.permits ?? []).length ||
      c.notes,
  );
});

const editing = ref(false);
const busy = ref(false);
const savedAt = ref<number | null>(null);
const errorMsg = ref<string | null>(null);
const customPpe = ref("");

const form = reactive<Required<WorkPackTaskBrief>>({
  description: "",
  location: "",
  supervisor: "",
  hazards: [],
  ppe: [],
  plant: [],
  permits: [],
  notes: "",
});

// Site hazards from the project register — pre-ticked onto new briefings and
// offered as one-click adds.
const activeHazards = computed<ProjectHazard[]>(() =>
  (props.hazards ?? []).filter((h) => !h.archived_at),
);
function hazardText(h: ProjectHazard): string {
  const head = h.location ? `${h.category} - ${h.location}` : h.category;
  return `${head}: ${h.description}`;
}
function addSiteHazard(h: ProjectHazard): void {
  const text = hazardText(h);
  if (!form.hazards.some((r) => r.hazard.trim() === text)) {
    form.hazards.push({ hazard: text, control: "" });
  }
}
function addAllSiteHazards(): void {
  for (const h of activeHazards.value) addSiteHazard(h);
}

function startEdit(): void {
  const b = props.content ?? {};
  form.description = b.description ?? "";
  form.location = b.location ?? "";
  form.supervisor = b.supervisor ?? "";
  form.hazards = (b.hazards ?? []).map((h) => ({ ...h }));
  form.ppe = [...(b.ppe ?? [])];
  form.plant = (b.plant ?? []).map((p) => ({ ...p }));
  form.permits = (b.permits ?? []).map((p) => ({ ...p }));
  form.notes = b.notes ?? "";
  // Pre-tick site hazards onto a fresh briefing (review + save).
  if (!form.hazards.length) addAllSiteHazards();
  errorMsg.value = null;
  customPpe.value = "";
  editing.value = true;
}

function togglePpe(item: string): void {
  const i = form.ppe.indexOf(item);
  if (i === -1) form.ppe.push(item);
  else form.ppe.splice(i, 1);
}
function addCustomPpe(): void {
  const v = customPpe.value.trim();
  if (v && !form.ppe.includes(v)) form.ppe.push(v);
  customPpe.value = "";
}

// Synthesise a spoken briefing from the content above (the RAMS) into Notes
// for the briefer to read out + edit. Grounded in the hazards/PPE/task.
const drafting = ref(false);
async function runDraft(): Promise<void> {
  drafting.value = true;
  errorMsg.value = null;
  try {
    const r = await draftBriefing({
      description: form.description.trim() || undefined,
      location: form.location.trim() || undefined,
      supervisor: form.supervisor.trim() || undefined,
      hazards: form.hazards.filter((h) => h.hazard.trim() || h.control.trim()),
      ppe: form.ppe,
      plant: form.plant.filter((p) => p.item.trim() || p.tag.trim()),
      permits: form.permits.filter((p) => p.type.trim() || p.reference.trim()),
    });
    form.notes = r.draft;
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    drafting.value = false;
  }
}

async function save(): Promise<void> {
  busy.value = true;
  errorMsg.value = null;
  try {
    const content: WorkPackTaskBrief = {
      description: form.description.trim() || undefined,
      location: form.location.trim() || undefined,
      supervisor: form.supervisor.trim() || undefined,
      hazards: form.hazards.filter((h) => h.hazard.trim() || h.control.trim()),
      ppe: form.ppe,
      plant: form.plant.filter((p) => p.item.trim() || p.tag.trim()),
      permits: form.permits.filter((p) => p.type.trim() || p.reference.trim()),
      notes: form.notes.trim() || undefined,
    };
    await updateBriefingContent(props.briefingId, content);
    savedAt.value = Date.now();
    editing.value = false;
    emit("saved");
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

const brief = computed(() => props.content);
</script>

<template>
  <section class="rounded-xl bg-neutral-0 shadow p-6">
    <div class="flex items-start justify-between gap-4">
      <h2 class="font-semibold">
        What was briefed
      </h2>
      <div class="flex items-center gap-3">
        <SaveStatus
          v-if="!editing"
          :saved-at="savedAt"
        />
        <button
          v-if="!editing"
          type="button"
          class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          @click="startEdit"
        >
          {{ present ? "Edit" : "+ Add content" }}
        </button>
      </div>
    </div>

    <!-- ===== Read view ===== -->
    <div
      v-if="!editing"
      class="mt-3"
    >
      <p
        v-if="!present"
        class="text-sm text-neutral-400"
      >
        Nothing briefed yet — add hazards, PPE, plant and permits the crew were
        briefed on.
      </p>
      <div
        v-else
        class="grid gap-4 text-sm"
      >
        <div v-if="brief?.description || brief?.location || brief?.supervisor">
          <p
            v-if="brief?.description"
            class="text-neutral-900"
          >
            {{ brief.description }}
          </p>
          <p class="mt-1 text-neutral-500">
            <span v-if="brief?.location">📍 {{ brief.location }}</span>
            <span v-if="brief?.location && brief?.supervisor"> · </span>
            <span v-if="brief?.supervisor">Supervisor: {{ brief.supervisor }}</span>
          </p>
        </div>

        <div v-if="(brief?.hazards ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Hazards &amp; controls
          </h3>
          <ul class="grid gap-1">
            <li
              v-for="(h, i) in brief?.hazards"
              :key="'h' + i"
              class="flex gap-2"
            >
              <span class="font-medium text-neutral-900">{{ h.hazard }}</span>
              <span
                v-if="h.control"
                class="text-neutral-500"
              >→ {{ h.control }}</span>
            </li>
          </ul>
        </div>

        <div v-if="(brief?.ppe ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Required PPE
          </h3>
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="item in brief?.ppe"
              :key="item"
              class="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-700"
            >{{ item }}</span>
          </div>
        </div>

        <div v-if="(brief?.plant ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Plant &amp; equipment
          </h3>
          <ul class="grid gap-1">
            <li
              v-for="(p, i) in brief?.plant"
              :key="'p' + i"
              class="flex gap-2"
            >
              <span class="font-medium text-neutral-900">{{ p.item }}</span>
              <span
                v-if="p.tag"
                class="text-neutral-500"
              >· {{ p.tag }}</span>
            </li>
          </ul>
        </div>

        <div v-if="(brief?.permits ?? []).length">
          <h3 class="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Permits
          </h3>
          <ul class="grid gap-1">
            <li
              v-for="(p, i) in brief?.permits"
              :key="'pm' + i"
              class="flex gap-2"
            >
              <span class="font-medium text-neutral-900">{{ p.type }}</span>
              <span
                v-if="p.reference"
                class="text-neutral-500"
              >· {{ p.reference }}</span>
            </li>
          </ul>
        </div>

        <p
          v-if="brief?.notes"
          class="whitespace-pre-line text-neutral-600"
        >
          {{ brief.notes }}
        </p>
      </div>
    </div>

    <!-- ===== Edit view ===== -->
    <div
      v-else
      class="mt-4 grid gap-5"
    >
      <!-- Task information -->
      <div class="grid gap-3">
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Work to be carried out</span>
          <textarea
            v-model="form.description"
            rows="2"
            placeholder="Describe the task for the shift…"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </label>
        <div class="grid gap-3 sm:grid-cols-2">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Exact location</span>
            <input
              v-model="form.location"
              type="text"
              placeholder="Loc 47, Up Main"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Supervisor</span>
            <input
              v-model="form.supervisor"
              type="text"
              placeholder="Name of the briefing supervisor"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
        </div>
      </div>

      <!-- Hazards & controls -->
      <div>
        <SiteHazardsNotice
          v-if="activeHazards.length"
          class="mb-3"
          :hazards="activeHazards"
          @add="addSiteHazard"
          @add-all="addAllSiteHazards"
        />
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-neutral-700">
            Hazards &amp; controls
          </h3>
          <button
            type="button"
            class="text-xs text-brand-primary hover:underline"
            @click="form.hazards.push({ hazard: '', control: '' })"
          >
            + Add hazard
          </button>
        </div>
        <p
          v-if="!form.hazards.length"
          class="text-sm text-neutral-400"
        >
          No hazards added.
        </p>
        <div
          v-for="(h, i) in form.hazards"
          :key="'eh' + i"
          class="mb-2 flex gap-2"
        >
          <input
            v-model="h.hazard"
            type="text"
            placeholder="Hazard"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="h.control"
            type="text"
            placeholder="Control measure / WMS"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <button
            type="button"
            class="text-neutral-300 hover:text-semantic-danger"
            title="Remove"
            @click="form.hazards.splice(i, 1)"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- PPE -->
      <div>
        <h3 class="mb-2 text-sm font-semibold text-neutral-700">
          Required PPE
        </h3>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="item in STANDARD_PPE"
            :key="item"
            type="button"
            class="rounded-full px-3 py-1 text-xs font-medium transition"
            :class="form.ppe.includes(item)
              ? 'bg-brand-primary text-brand-primary-on'
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
            @click="togglePpe(item)"
          >
            {{ item }}
          </button>
          <button
            v-for="item in form.ppe.filter((p) => !STANDARD_PPE.includes(p))"
            :key="'c' + item"
            type="button"
            class="rounded-full bg-brand-primary px-3 py-1 text-xs font-medium text-brand-primary-on"
            @click="togglePpe(item)"
          >
            {{ item }} ✕
          </button>
        </div>
        <div class="mt-2 flex gap-2">
          <input
            v-model="customPpe"
            type="text"
            placeholder="Add other PPE…"
            class="w-48 rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
            @keyup.enter="addCustomPpe"
          >
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
            @click="addCustomPpe"
          >
            Add
          </button>
        </div>
      </div>

      <!-- Plant & equipment -->
      <div>
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-neutral-700">
            Plant &amp; equipment
          </h3>
          <button
            type="button"
            class="text-xs text-brand-primary hover:underline"
            @click="form.plant.push({ item: '', tag: '' })"
          >
            + Add item
          </button>
        </div>
        <p
          v-if="!form.plant.length"
          class="text-sm text-neutral-400"
        >
          No plant added.
        </p>
        <div
          v-for="(p, i) in form.plant"
          :key="'ep' + i"
          class="mb-2 flex gap-2"
        >
          <input
            v-model="p.item"
            type="text"
            placeholder="Plant / equipment"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="p.tag"
            type="text"
            placeholder="Inspection / tag status"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <button
            type="button"
            class="text-neutral-300 hover:text-semantic-danger"
            title="Remove"
            @click="form.plant.splice(i, 1)"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Permits -->
      <div>
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-neutral-700">
            Permits &amp; sign-offs
          </h3>
          <button
            type="button"
            class="text-xs text-brand-primary hover:underline"
            @click="form.permits.push({ type: '', reference: '' })"
          >
            + Add permit
          </button>
        </div>
        <p
          v-if="activeHazards.length"
          class="mb-2 text-xs text-semantic-warning"
        >
          ⚠ {{ activeHazards.length }} site hazard(s) recorded — ensure the
          permits cover them.
        </p>
        <p
          v-if="!form.permits.length"
          class="text-sm text-neutral-400"
        >
          No permits added.
        </p>
        <div
          v-for="(p, i) in form.permits"
          :key="'epm' + i"
          class="mb-2 flex gap-2"
        >
          <input
            v-model="p.type"
            type="text"
            placeholder="Permit type (hot work, confined space…)"
            class="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="p.reference"
            type="text"
            placeholder="Reference / number"
            class="w-40 rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <button
            type="button"
            class="text-neutral-300 hover:text-semantic-danger"
            title="Remove"
            @click="form.permits.splice(i, 1)"
          >
            ✕
          </button>
        </div>
      </div>

      <label class="grid gap-1 text-sm">
        <div class="flex items-center justify-between">
          <span class="text-neutral-500">Notes</span>
          <button
            type="button"
            class="rounded-md border border-brand-primary/40 bg-brand-primary/5 px-2.5 py-1 text-xs font-medium text-brand-primary hover:bg-brand-primary/10 disabled:opacity-50"
            :disabled="drafting"
            title="Draft a spoken briefing from the hazards, PPE and task above"
            @click="runDraft"
          >
            {{ drafting ? "Drafting…" : "✨ Draft briefing" }}
          </button>
        </div>
        <textarea
          v-model="form.notes"
          rows="4"
          placeholder="Anything else to brief…"
          class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
        />
      </label>

      <div class="flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="busy"
          @click="save"
        >
          {{ busy ? "Saving…" : "Save content" }}
        </button>
        <button
          type="button"
          class="text-sm text-neutral-500 hover:text-neutral-900"
          @click="editing = false"
        >
          Cancel
        </button>
        <SaveStatus
          :saving="busy"
          :saved-at="savedAt"
        />
        <span
          v-if="errorMsg"
          class="text-sm text-semantic-danger"
        >{{ errorMsg }}</span>
      </div>
    </div>
  </section>
</template>
